import { ReactElement } from 'react';
import { Input, Modal, ModalFooter } from '@sc/shared-ui';
import { useMutation } from '@apollo/client/react';
import { useForm } from '@tanstack/react-form';
import {
  CompanyUserRole,
  CompanyUserRowFragment,
} from '@sc/graphql';
import { GQL_MUTATION_UPDATE_COMPANY_USER } from '../utility/queries';
import { useAlerts } from '../providers/alerts';
import { notify } from '../utility/notify';
import { track } from '../utility/analytics';
import { usersUtility } from '../utility/users';
import { Tile } from '../components/Tile';

export const EditUserModal = ({
  companyUser,
  onClose,
}: {
  companyUser: CompanyUserRowFragment | null;
  onClose: () => void;
}): ReactElement | null => {
  const { useApolloError } = useAlerts();
  const onError = useApolloError();

  const [updateCompanyUser, { loading }] = useMutation(
    GQL_MUTATION_UPDATE_COMPANY_USER
  );

  const form = useForm({
    defaultValues: {
      name: companyUser?.user.name ?? '',
      email: companyUser?.user.email ?? '',
      role: companyUser?.role ?? CompanyUserRole.member,
    },
    onSubmit: ({ value }) => {
      if (!companyUser) return;

      const emailChanged = value.email !== companyUser.user.email;

      const roleChanged = value.role !== companyUser.role;

      void updateCompanyUser({
        variables: {
          input: {
            userUuid: companyUser.userUuid,
            name: value.name,
            email: emailChanged ? value.email : null,
            role: value.role,
          },
        },
        onCompleted: () => {
          track('User Updated', { roleChanged, toRole: value.role });

          notify.success(
            emailChanged
              ? 'User updated — verification email sent'
              : 'User updated'
          );

          onClose();
        },
        onError,
      });
    },
  });

  return (
    <Modal
      open={!!companyUser}
      onClose={onClose}
      title="Edit user"
      dialogPanelClassname="w-[520px]"
      loading={loading}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();

          e.stopPropagation();

          void form.handleSubmit();
        }}
      >
        <div className="p-5 space-y-5">
          <form.Field
            name="name"
            children={({ state, handleChange }) => (
              <Input
                label="Name"
                value={state.value}
                required
                onChange={(e) => handleChange(e.target.value)}
              />
            )}
          />
          <form.Field
            name="email"
            children={({ state, handleChange }) => (
              <Input
                label="Email"
                type="email"
                value={state.value}
                required
                helperText="Changing email will trigger a verification email to the new address."
                onChange={(e) => handleChange(e.target.value)}
              />
            )}
          />
          <div className="space-y-2">
            <span className="text-body-medium font-semibold block">
              Member access level
            </span>
            <form.Field
              name="role"
              children={({ state, handleChange }) => (
                <div className="flex gap-2">
                  {usersUtility.roleTiles.map((tile) => (
                    <Tile
                      key={tile.role}
                      Icon={tile.Icon}
                      label={usersUtility.companyUserRoleDisplayTitles[tile.role]}
                      description={tile.description}
                      checked={state.value === tile.role}
                      onChange={() => handleChange(tile.role)}
                    />
                  ))}
                </div>
              )}
            />
          </div>
        </div>
        <ModalFooter
          asForm
          buttonsDisabled={loading}
          onClose={onClose}
          submitText="Save"
        />
      </form>
    </Modal>
  );
};
